import { WIZ_COMMAND_STATE, WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp, WizRoot, WizEventManager } from '@wizeoo/akm-component-lib';
import { PhysicalTemplate, EntityModificationPack, BusinessException } from '@wizeoo/akm-message-lib';
import { Model } from '../../model/model';
import { PhysicalTemplateVO } from '../../vo/workingzone/physical-template.vo';
import { CompactType, DisplayGrid, GridType } from 'angular-gridster2';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE } from '../../model/enums';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';
import { cloneDeep } from 'lodash-es';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { GridsterWidget } from '../../vo/workingzone/gridster-widget';


/** gestion des modèles de vue */
export class PhysicalTemplateEngine {
  
  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }
  
  /** création d'un nouvel élément */
  public newPhysicalTemplate(): PhysicalTemplate {
    const phyT: PhysicalTemplate = new PhysicalTemplate();
    phyT.domain = this.model.domainCode;
    phyT.businessUnitCode = this.model.businessUnitCode;
    phyT.visionCode = this.model.visionCode;
    return phyT;
  }

  /**
   * alimentation du modèle sur retour de service
   * @param entities 
   */
  public feed(entities: PhysicalTemplate[]): void {
    let vos: PhysicalTemplateVO[] = this.entityToVO(entities);
    this.model.physicalTemplateCollection = vos;

    // Map des physical templates par bu = vision = code = template
    const newDic: Map<string, Map<string, Map<string, PhysicalTemplateVO>>> = new Map<string, Map<string, Map<string, PhysicalTemplateVO>>>();
      if (vos) {
        vos.forEach((phyt: PhysicalTemplateVO) => {
          if (!newDic.has(phyt.businessUnitCode)) {
            newDic.set(phyt.businessUnitCode, new Map<string, Map<string, PhysicalTemplateVO>>());
          }
          if (!newDic.get(phyt.businessUnitCode).has(phyt.visionCode)) {
            newDic.get(phyt.businessUnitCode).set(phyt.visionCode, new Map<string, PhysicalTemplateVO>());
          }
          newDic.get(phyt.businessUnitCode).get(phyt.visionCode).set(phyt.code, phyt);
        });
      }
      this.model.physicalTemplateMap = newDic;
  }

  /**
   * Effectue un clone du physical template
   * @param src La source à cloner
   * @return le clone
   */
  public clone(src: PhysicalTemplateVO): PhysicalTemplateVO {
    let res: PhysicalTemplateVO = new PhysicalTemplateVO(this.model, cloneDeep(src.source));
    return res;
  }

  /**
   * encapsulation d'une collection en collection de VOs
   * @param source  collection de PhysicalTemplate
   * @returns collection de PhysicalTemplateVO
   */
  public entityToVO(source: PhysicalTemplate[]): PhysicalTemplateVO[] {
    const result: PhysicalTemplateVO[] = [];
    for (const item of source) {
      result.push(new PhysicalTemplateVO(this.model, item));
    }
    return result;
  }

  /** retourne les templates de la vision en cours */
  public getVisionTemplates(visionCode: string): PhysicalTemplateVO[] {
    // const map = this.model.physicalTemplateMap;
    if (
      this.model.physicalTemplateMap &&
      this.model.physicalTemplateMap.has(this.model.businessUnitCode) &&
      this.model.physicalTemplateMap.get(this.model.businessUnitCode).has(visionCode)
    ) {
      return Array.from(
        this.model.physicalTemplateMap.get(this.model.businessUnitCode).get(visionCode).values()
      );
    }
    return [];
  }

  /** retourne l'instance pour un template, sur la vision en cours */
  public getTemplate(code: string): PhysicalTemplateVO {
    const map = this.model.physicalTemplateMap;
    let result: PhysicalTemplateVO = null;
    if (
      this.model.physicalTemplateMap &&
      this.model.physicalTemplateMap.has(this.model.businessUnitCode) &&
      this.model.physicalTemplateMap.get(this.model.businessUnitCode)
        .has(this.model.visionCode)
    ) {
      result = this.model.physicalTemplateMap.get(this.model.businessUnitCode)
        .get(this.model.visionCode)
        .get(code);
    }
    return result;
  }

  /**
   * Ajoute un templateVO à la collection et à la map
   * @param vo à ajouter
   */
  public addTemplate(phyt: PhysicalTemplateVO): void {
    this.model.physicalTemplateCollection.push(phyt); // ajout à la collection
    const dic: Map<string, Map<string, Map<string, PhysicalTemplateVO>>> = this.model.physicalTemplateMap;
    if (!dic.has(phyt.businessUnitCode)) {
      dic.set(phyt.businessUnitCode, new Map<string, Map<string, PhysicalTemplateVO>>());
    }
    if (!dic.get(phyt.businessUnitCode).has(phyt.visionCode)) {
      dic.get(phyt.businessUnitCode).set(phyt.visionCode, new Map<string, PhysicalTemplateVO>());
    }
    dic.get(phyt.businessUnitCode).get(phyt.visionCode).set(phyt.code, phyt);
  }

  /**
   * supprimer un templateVO
   * @param vo à supprimer
   */
  public removeTemplate(vo: PhysicalTemplateVO): void {
    this.model.physicalTemplateMap.get(vo.businessUnitCode).get(vo.visionCode).delete(vo.code);
    this.model.physicalTemplateCollection.splice(
      this.model.physicalTemplateCollection.indexOf(vo),
      1
    );
  }

  /**
   * create/ update d'un template
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: PhysicalTemplateVO, from: PhysicalTemplateVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exists()) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (!!pack && !!pack.ems && pack.ems.length > 0) { // TODO attention taille modif pour ems
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * Suppression d'un template
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: PhysicalTemplateVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: PhysicalTemplateVO = cmd.sourceVO[0] as PhysicalTemplateVO;
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const modelPhyT: PhysicalTemplateVO = this.getTemplate(vo.code);
            if (!!modelPhyT) {
              modelPhyT.source = source;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.addTemplate(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this.removeTemplate(vo);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }

  /** configuration minimale gridster */
  public static gridsterSafe(): any {
    return {
      gridType: GridType.Fixed,
      compactType: CompactType.None,
      draggable: { enabled: true },
      resizable: { enabled: true },
      pushDirections: { north: true, east: true, south: true, west: true },
    };
  }

  /** isolation de la configuration gridster pour ne pas polluer la vue templating */
  public gridsterOption(): any {
    const options = {
      gridType: GridType.Fixed,
      compactType: CompactType.None,
      margin: 5,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      useTransformPositioning: true,
      mobileBreakpoint: 640,
      minCols: 5,
      maxCols: 200,
      minRows: 5,
      maxRows: 200,
      maxItemCols: 200,
      minItemCols: 2,
      maxItemRows: 100,
      minItemRows: 2,
      maxItemArea: 20000,
      minItemArea: 4,
      defaultItemCols: 30,
      defaultItemRows: 20,
      fixedColWidth: 10,
      fixedRowHeight: 10,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      enableOccupiedCellDrop: false,
      emptyCellDragMaxCols: 500,
      emptyCellDragMaxRows: 500,
      ignoreMarginInRow: false,
      draggable: {
        enabled: true,
      },
      resizable: {
        enabled: true,
        handles: {
          se: true, // coin bas-droite
          ne: true, // coin haut-droite
          sw: true, // coin bas-gauche
          nw: true, // coin haut-gauche

          // Désactive tous les bords
          n: false,
        }
      },
      swap: false,
      pushItems: false,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: true, east: true, south: true, west: true },
      pushResizeItems: false,
      displayGrid: DisplayGrid.OnDragAndResize,
      disableWindowResize: false,
      disableWarnings: false,
      scrollToNewItems: false,
    };

    return options;
  }

  /** régénère correctement les indices sur les widgets */
  public resetIndices(agis: GridsterWidget[]): void {
    if(!agis) {
      return;
    }
    let idx: number = 1;
    agis
      .sort((a, b) => a.coords - b.coords)
      .forEach((agi: GridsterWidget) => {
        agi.indice = idx;
        idx++;
      });
    // forcer le refresh sur les widgets
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_CHANGE_SEQUENCE, agis));
  }


  /**
   * Effectue un clone du physical template avec un nouveau code
   * @param src La source à cloner
   * @return le clone
   */
  public CloneAndPersist(src: PhysicalTemplateVO, newCode: string): PhysicalTemplateVO {
    let result: PhysicalTemplateVO = new PhysicalTemplateVO(this.model, cloneDeep(src.source));
    result.code = newCode;
    result.uid = WizMathUtils.createUUID();
    result.id = null;

    return result;
  }

}
